import EventScope from 'owa-service/lib/contract/EventScope';
import getCalendarEventByItemIdFromCache from '../store/apis/getCalendarEventByItemIdFromCache';
import getItemIdsByInstanceKeyFromCache from '../store/apis/getItemIdsByInstanceKeyFromCache';
import getItemIdToUpdate from 'mini-calendar-event-services/lib/utils/getItemIdToUpdate';
import itemChange from 'owa-service/lib/factory/itemChange';
import updateCalendarEventByItemIdsAndNotify from '../store/apis/updateCalendarEventsByItemIdsAndNotify';
import updateCalendarEventService from '../services/updateCalendarEventService';
import UpdateItemResponseMessage from 'owa-service/lib/contract/UpdateItemResponseMessage';
import { CalendarEvent } from 'mini-calendar-event-types';
import { ClientItemId } from 'owa-client-ids';
import { getUpdateForProperty } from '../utils/getUpdateForProperty';

/**
 * Updates the calendar event in the server and in the events cache
 * @param itemId Id of the event that we want to update
 * @param calendarEventUpdates The updates to the calendar event
 * @param eventScope The scope/instance of event(like single instance, series, etc.)
 * @param isLocalUpdateOnly if true, will perform only local cache update without performing service calls
 * @returns Returns the status of update operation, a true is success, false for failure.
 */
export default async function updateCalendarEvent(
    itemId: ClientItemId,
    calendarEventUpdates: Partial<CalendarEvent>,
    eventScope: EventScope,
    isLocalUpdateOnly: boolean = false
): Promise<boolean> {
    // Get updates in the required format
    let response: UpdateItemResponseMessage;
    const { Id: id, mailboxInfo } = itemId;

    // First determine if the updates are to be done or not
    const updates = Object.keys(calendarEventUpdates).length
        ? Object.keys(calendarEventUpdates)
              .map((key: keyof CalendarEvent) =>
                  getUpdateForProperty(key, calendarEventUpdates[key])
              )
              .filter(Boolean)
        : [];

    if (!updates.length) {
        Promise.resolve(true);
    }

    if (!isLocalUpdateOnly) {
        // If we are not doing a local update only, make the service call

        const exchangeItemIdToUpdate = getItemIdToUpdate(id, eventScope);

        // Notify the server to update the item
        response = await updateCalendarEventService(
            exchangeItemIdToUpdate,
            mailboxInfo,
            itemChange({ Updates: updates, ItemId: exchangeItemIdToUpdate }),
            eventScope
        );
    }

    if ((response && response.ResponseClass === 'Success') || isLocalUpdateOnly) {
        // Now perform the local updates
        let itemIdsToUpdate = [];

        // Get the id to update from the scope of the item
        if (eventScope === EventScope.AllInstancesInSeries) {
            const item = getCalendarEventByItemIdFromCache(id);
            itemIdsToUpdate = item ? getItemIdsByInstanceKeyFromCache(item.InstanceKey) : [];
        } else {
            itemIdsToUpdate = [id];
        }

        // update locally
        updateCalendarEventByItemIdsAndNotify(
            itemIdsToUpdate,
            calendarEventUpdates,
            (item: CalendarEvent) => item && !item.IsSeriesInstanceException
        );
        return Promise.resolve(true);
    } else {
        return Promise.resolve(false);
    }
}
